// /app/frontend/src/components/ui/calendar-input.jsx
import React, { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * CalendarInput Component - Date Input with Dropdown Calendar
 *
 * Features:
 * - Direct typing: DD/MM/YYYY format
 * - Dropdown calendar on click/focus
 * - Month/year navigation
 * - Both input and calendar stay in sync
 * - Works on all screen sizes
 * - Auto-fill today's date option
 *
 * @param {string} value - Date string in YYYY-MM-DD format
 * @param {function} onChange - Callback when date changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} defaultToday - Auto-set to today if empty on mount
 * @param {boolean} disabled - Disable input
 */
export function CalendarInput({
  value,
  onChange,
  placeholder = 'DD/MM/YYYY',
  defaultToday = false,
  disabled = false,
  className,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Initialize from value prop
  React.useEffect(() => {
    if (value) {
      // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM:SS" formats
      let dateStr = value;
      if (dateStr.includes('T')) {
        // Extract date part only from ISO datetime string
        dateStr = dateStr.split('T')[0];
      }
      const date = new Date(dateStr + 'T00:00:00');
      if (isValid(date)) {
        setInputValue(format(date, 'dd/MM/yyyy'));
        setCalendarMonth(date);
      }
    } else {
      setInputValue('');
    }
  }, [value]);

  // Auto-set to today if defaultToday and no value (only on mount)
  React.useEffect(() => {
    if (defaultToday && (!value || value === '')) {
      const today = new Date();
      const todayString = format(today, 'yyyy-MM-dd');
      onChange(todayString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle typing in input field
   */
  const handleInputChange = (e) => {
    const input = e.target.value.trim();
    setInputValue(input);

    // Allow empty input
    if (!input) {
      onChange('');
      return;
    }

    // Only parse if complete (10 chars: dd/mm/yyyy)
    if (input.length === 10) {
      const parsed = parse(input, 'dd/MM/yyyy', new Date());
      if (isValid(parsed)) {
        const formattedDate = format(parsed, 'yyyy-MM-dd');
        onChange(formattedDate);
        setCalendarMonth(parsed);
      }
    }
  };

  /**
   * Handle blur - validate and reset if needed
   */
  const handleInputBlur = () => {
    if (!inputValue) {
      onChange('');
      return;
    }

    if (inputValue.length === 10) {
      const parsed = parse(inputValue, 'dd/MM/yyyy', new Date());
      if (!isValid(parsed)) {
        // Reset to last valid value or clear
        if (value) {
          let dateStr = value;
          if (dateStr.includes('T')) {
            dateStr = dateStr.split('T')[0];
          }
          const date = new Date(dateStr + 'T00:00:00');
          setInputValue(format(date, 'dd/MM/yyyy'));
        } else {
          setInputValue('');
          onChange('');
        }
      }
    } else if (inputValue.length > 0) {
      // Incomplete - reset on blur
      if (value) {
        let dateStr = value;
        if (dateStr.includes('T')) {
          dateStr = dateStr.split('T')[0];
        }
        const date = new Date(dateStr + 'T00:00:00');
        setInputValue(format(date, 'dd/MM/yyyy'));
      } else {
        setInputValue('');
        onChange('');
      }
    }
  };

  /**
   * Handle day click in calendar
   */
  const handleDayClick = (day) => {
    const newDate = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      day
    );
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    onChange(formattedDate);
    setInputValue(format(newDate, 'dd/MM/yyyy'));
    setIsOpen(false);
  };

  /**
   * Navigate calendar months
   */
  const previousMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1));
  };

  /**
   * Generate calendar days
   */
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const daysInMonth = getDaysInMonth(calendarMonth);
  const firstDay = getFirstDayOfMonth(calendarMonth);
  const days = [];

  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM:SS" formats for selectedDate
  let selectedDateStr = value;
  if (selectedDateStr && selectedDateStr.includes('T')) {
    selectedDateStr = selectedDateStr.split('T')[0];
  }
  const selectedDate = selectedDateStr ? new Date(selectedDateStr + 'T00:00:00') : null;
  const isSelectedMonth =
    selectedDate &&
    selectedDate.getFullYear() === calendarMonth.getFullYear() &&
    selectedDate.getMonth() === calendarMonth.getMonth();

  return (
    <div className={cn('w-full', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              type="text"
              inputMode="numeric"
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              disabled={disabled}
              className="pr-10 text-sm"
              maxLength="10"
            />
            <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>
        </PopoverTrigger>

        {/* Calendar Dropdown */}
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-3">
            {/* Month/Year Header */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={previousMonth}
                className="p-1 hover:bg-muted rounded"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="text-sm font-semibold">
                {format(calendarMonth, 'MMMM yyyy')}
              </div>
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 hover:bg-muted rounded"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Day Labels */}
            <div className="grid grid-cols-7 gap-1">
              {dayLabels.map((label) => (
                <div
                  key={label}
                  className="h-8 w-8 flex items-center justify-center text-xs font-semibold text-muted-foreground"
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => day && handleDayClick(day)}
                  disabled={!day}
                  className={cn(
                    'h-8 w-8 rounded text-sm font-medium transition-colors',
                    !day && 'invisible',
                    day &&
                      isSelectedMonth &&
                      selectedDate.getDate() === day
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  )}
                >
                  {day}
                </button>
              ))}
            </div>

            {/* Clear Button */}
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setInputValue('');
                  setIsOpen(false);
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground py-1 rounded hover:bg-muted"
              >
                Clear
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Formatted Date Display */}
      {inputValue && value && (
        <p className="text-xs text-muted-foreground mt-1">
          {(() => {
            let dateStr = value;
            if (dateStr.includes('T')) {
              dateStr = dateStr.split('T')[0];
            }
            const date = new Date(dateStr + 'T00:00:00');
            return isValid(date) ? format(date, 'EEEE, MMMM d, yyyy') : '';
          })()}
        </p>
      )}
    </div>
  );
}

export default CalendarInput;
